import { useToast } from '../../contexts/ToastContext'
import { cn } from '../../lib/cn'

type EnabledVariant = 'ink' | 'accent' | 'approve'

interface GatedButtonProps {
  /** When true the control is styled as gated and clicking flashes the hint. */
  gated: boolean
  /** Disabled while an operation is in flight; gate hints remain clickable otherwise. */
  disabled?: boolean
  /** Toast copy shown when a gated control is clicked. */
  hint: string
  onClick?: () => void
  variant?: EnabledVariant
  children: React.ReactNode
  className?: string
}

const enabledStyles: Record<EnabledVariant, string> = {
  ink: 'bg-ink text-paper-panel hover:bg-ink/90',
  accent: 'bg-terra text-paper-surface hover:bg-terra-deep',
  approve: 'bg-moss text-paper-surface hover:bg-moss/90',
}

/**
 * A button that is never a dead disabled control. When `gated`, it keeps a muted
 * look and, on click, flashes its gate hint as a toast instead of acting.
 */
export function GatedButton({ gated, disabled = false, hint, onClick, variant = 'ink', children, className }: GatedButtonProps) {
  const { flash } = useToast()

  return (
    <button
      type="button"
      disabled={disabled}
      aria-disabled={disabled || gated}
      onClick={() => (gated ? flash(hint) : onClick?.())}
      className={cn(
        'inline-flex items-center justify-center rounded-btn px-[18px] py-3 font-sans text-[13.5px] font-medium leading-none transition-colors',
        gated ? 'bg-[#e5dbc9] text-[#9c8f7e]' : enabledStyles[variant],
        className,
      )}
    >
      {children}
    </button>
  )
}
